import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { formatNumber } from "./validation";
import { AI_REPORT_USER_BEHAVIOUR } from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";

const UserBehaviorTab = ({ startDate, endDate }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    newUsers: 0,
    returningUsers: 0,
    retentionRate: 0,
    avgSessionDuration: 0,
  });

  const [dailyUsers, setDailyUsers] = useState([]);
  const [conversationLength, setConversationLength] = useState([]);
  const [peakHours, setPeakHours] = useState([]);

  const [engagementMetrics, setEngagementMetrics] = useState([]);

  const formatHour = (hour) => {
    const h = Number(hour);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:00 ${suffix}`;
  };

  const formatMessageCount = (count) => {
    return `${count} ${count === 1 ? "Message" : "Messages"}`;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();

      const response = await fetch(
        `${AI_REPORT_USER_BEHAVIOUR}?from_date=${startDate}&to_date=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch user behaviour data");

      const json = await response.json();
      if (!json.success) throw new Error("API returned failure");

      const data = json.data;

      // ---- Top stats ----
      setStats({
        newUsers: data.new_users,
        returningUsers: data.returning_users,
        retentionRate: data.retention_rate,
        avgSessionDuration: data.average_session_duration || 0,
      });

      // ---- Charts ----
      setDailyUsers(data.daily_active_users || []);
      setConversationLength(data.conversation_length_distribution || []);
      setPeakHours(data.peak_usage_hours || []);

      // ---- Engagement table ----
      setEngagementMetrics([
        {
          metric: "Avg Messages / Conversation",
          value: data.average_messages_per_conversation,
          status:
            data.average_messages_per_conversation >= 5 ? "Good" : "Average",
          description:
            "Average number of messages exchanged per conversation with the AI",
        },
        {
          metric: "Conversation Completion Rate",
          value: `${data.conversation_complete_rate}%`,
          status:
            data.conversation_complete_rate >= 80 ? "Excellent" : "Average",
          description:
            "Percentage of conversations that reached a natural completion without abandonment",
        },
        {
          metric: "Drop-off Rate",
          value: `${data.drop_off_rate}%`,
          status: data.drop_off_rate <= 20 ? "Good" : "Warning",
          description:
            "Percentage of conversations where users left before completion",
        },
      ]);
    } catch (err) {
      console.error(err);
      setError("Unable to load user behaviour data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      {/* ---- TOP METRICS ---- */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Number of users who interacted with the AI for the first time in the selected period">
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">New Users</Typography>
              <Typography variant="h4">
                {formatNumber(stats.newUsers)}
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Number of users who have used the AI before and returned in the selected period">
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Returning Users</Typography>
              <Typography variant="h4">
                {formatNumber(stats.returningUsers)}
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Percentage of users in the current period who were also active in previous periods">
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Retention Rate</Typography>
              <Typography variant="h4" color="success.main">
                {stats.retentionRate}%
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Average duration of a user session in seconds. Calculated from the start to the last message">
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Avg Session Duration</Typography>
              <Typography variant="h4">
                {stats.avgSessionDuration || 0}s
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>

      {/* ---- DAILY ACTIVE USERS ---- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6">Daily Active Users</Typography>
        <Box sx={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyUsers}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <RechartsTooltip formatter={(value) => [`${value}`, "Users"]} />
              <Line dataKey="users" stroke="#16a34a" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ---- CONVERSATION LENGTH ---- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6">Conversation Length Distribution</Typography>
        <Box sx={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={conversationLength}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="message_count" />
              <YAxis />
              <RechartsTooltip
                labelFormatter={(value) => formatMessageCount(value)}
                formatter={(value) => [`${value}`, "Conversations"]}
              />
              <Bar dataKey="count" fill="#9333ea" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ---- PEAK HOURS ---- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6">Peak Usage Hours</Typography>
        <Box sx={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={peakHours}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tickFormatter={formatHour} />
              <YAxis />
              <RechartsTooltip
                labelFormatter={(value) => formatHour(value)}
                formatter={(value) => [`${value}`, "Sessions"]}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#f59e0b"
                strokeWidth={2}
                name="Sessions"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ---- ENGAGEMENT TABLE ---- */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">User Engagement Metrics</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {engagementMetrics.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Tooltip title={row.description}>{row.metric}</Tooltip>
                  </TableCell>
                  <TableCell align="right">{row.value}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === "Excellent" || row.status === "Good"
                          ? "success"
                          : "warning"
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default UserBehaviorTab;
